
import { supabase } from '@/integrations/supabase/client';

export interface InvitationData {
  inviterName: string;
  inviterEmail: string;
  referralCode: string;
  invitedEmails: string[];
}

export class ReferralService {
  static async checkExistingInvitations(userId: string, emails: string[]): Promise<string[]> {
    try {
      console.log('Checking existing invitations for emails:', emails);
      
      // Filter out empty emails first
      const validEmails = emails.filter(email => email.trim() !== '');
      
      if (validEmails.length === 0) {
        console.log('No valid emails to check');
        return [];
      }

      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

      const { data: existingInvitations, error } = await supabase
        .from('email_invitations')
        .select('invited_email')
        .in('invited_email', validEmails)
        .gt('sent_at', twentyFourHoursAgo.toISOString())
        .eq('user_id', userId);

      if (error) {
        console.error('Error checking existing invitations:', error);
        throw error;
      }

      // Convert existing invitations to a Set for faster lookups
      const recentlyInvitedEmails = new Set(existingInvitations?.map(inv => inv.invited_email) || []);
      
      // Filter out recently invited emails
      const newEmails = validEmails.filter(email => !recentlyInvitedEmails.has(email));

      console.log('Filtered emails:', {
        total: validEmails.length,
        new: newEmails.length,
        existing: recentlyInvitedEmails.size,
        filtered: newEmails
      });

      return newEmails;
    } catch (error) {
      console.error('Error in checkExistingInvitations:', error);
      throw error;
    }
  }

  static async createInvitations(userId: string, invitationData: InvitationData): Promise<void> {
    console.log('Creating invitations:', {
      userId,
      emailCount: invitationData.invitedEmails.length,
      emails: invitationData.invitedEmails
    });

    if (invitationData.invitedEmails.length === 0) {
      console.log('No emails to create invitations for');
      return;
    }

    const invitationsData = invitationData.invitedEmails.map(email => ({
      user_id: userId,
      inviter_name: invitationData.inviterName,
      inviter_email: invitationData.inviterEmail,
      invited_email: email.trim(),
      referral_code: invitationData.referralCode,
      status: 'sent'
    }));

    const { error: dbError } = await supabase
      .from('email_invitations')
      .insert(invitationsData);

    if (dbError) {
      console.error('Error creating invitations:', dbError);
      throw dbError;
    }

    // Get total invitations count for this user
    const { data: totalInvitations, error: countError } = await supabase
      .from('email_invitations')
      .select('invited_email')
      .eq('user_id', userId);

    if (countError) {
      console.error('Error counting invitations:', countError);
      return;
    }

    const totalCount = totalInvitations?.length || 0;
    console.log('Total invitations count:', totalCount);

    // Check if we've reached a milestone (multiple of 10)
    if (totalCount >= 10 && totalCount % 10 === 0) {
      console.log('Milestone reached:', totalCount);
      try {
        // Directly call the edge function for immediate notification
        const { error: notifyError } = await supabase.functions.invoke(
          'notify-referral-milestone',
          {
            body: JSON.stringify({
              userId,
              invitedEmails: invitationData.invitedEmails,
              totalInvitations: totalCount
            })
          }
        );

        if (notifyError) {
          console.error('Error notifying milestone:', notifyError);
        }

      } catch (error) {
        console.error('Error notifying referral milestone:', error);
      }
    }

    console.log('Successfully created invitations in database');
  }

  static async sendInvitationEmails(invitationData: InvitationData): Promise<void> {
    console.log('Sending invitation emails:', {
      emailCount: invitationData.invitedEmails.length,
      emails: invitationData.invitedEmails
    });

    if (invitationData.invitedEmails.length === 0) {
      console.log('No emails to send invitations to');
      return;
    }

    const { error: emailError, data } = await supabase.functions.invoke(
      'send-invitation-emails',
      {
        body: JSON.stringify(invitationData)
      }
    );

    console.log('Edge function response:', data);

    if (emailError) {
      console.error('Error sending invitation emails:', emailError);
      throw emailError;
    }

    if (data?.error) {
      console.error('Edge function returned error:', data.error);
      throw new Error(data.error);
    }

    console.log('Successfully sent all invitation emails');
  }
}
